import { ChevronDown, ChevronUp, GripVertical, Trash2 } from "lucide-react";
import React from "react";
import { createPortal } from "react-dom";
import { FormattedMessage, useIntl } from "react-intl";
import { AutoResizeTextarea } from "~/components/ui/auto-resize-textarea";
import { Button } from "~/components/ui/button";
import { Checkbox } from "~/components/ui/checkbox";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { ScrollArea } from "~/components/ui/scroll-area";
import { deserializeColor } from "~/features/settings/components/categories/ColorPicker";
import { CategoryOption } from "./CategorySelectionModal";
import { EditableSection } from "./DraggableSections";
import { MAX_SECTION_CHARS } from "./helpers";

interface SectionItemProps {
  section: EditableSection;
  provided: any; // DraggableProvided is not exported from @hello-pangea/dnd
  isDragging: boolean;
  onUpdate: (
    id: string,
    field: keyof Omit<EditableSection, "id" | "order">,
    value: string
  ) => void;
  onRemove: (id: string) => void;
  onToggleCollapse: (id: string) => void;
  isCollapsed: boolean;
  isRemoveDisabled: boolean;
  showError: boolean;
  parsingError: boolean;
  availableCategories?: CategoryOption[];
  onCategoryToggle?: (sectionId: string, categoryName: string) => void;
  onSelectAllCategories?: (sectionId: string) => void;
  onClearAllCategories?: (sectionId: string) => void;
}

const hexToRgba = (hex: string, opacity: number): string => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (result) {
    const r = parseInt(result[1], 16);
    const g = parseInt(result[2], 16);
    const b = parseInt(result[3], 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }
  return `rgba(229, 231, 235, ${opacity})`;
};

export const SectionItem: React.FC<SectionItemProps> = ({
  section,
  provided,
  isDragging,
  onUpdate,
  onRemove,
  onToggleCollapse,
  isCollapsed,
  isRemoveDisabled,
  showError,
  parsingError,
  availableCategories = [],
  onCategoryToggle,
  onSelectAllCategories,
  onClearAllCategories,
}) => {
  const intl = useIntl();

  const excludedNames = section.config?.excludedCategoryNames || [];
  const selectedCount = availableCategories.length - excludedNames.length;
  const allSelected = excludedNames.length === 0;
  const noneSelected = excludedNames.length === availableCategories.length;

  const handleSelectAll = () => {
    if (onSelectAllCategories && section.sectionType === "docList") {
      onSelectAllCategories(section.id);
    }
  };

  const handleClearAll = () => {
    if (onClearAllCategories && section.sectionType === "docList") {
      onClearAllCategories(section.id);
    }
  };

  const content = (
    <div
      ref={provided.innerRef}
      {...provided.draggableProps}
      className={`p-4 border rounded-lg space-y-4 bg-background ${
        isDragging ? "shadow-lg" : ""
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 flex-1">
          <div
            {...provided.dragHandleProps}
            className="cursor-grab active:cursor-grabbing p-1 hover:bg-muted rounded"
          >
            <GripVertical className="w-4 h-4 text-muted-foreground" />
          </div>
          <Input
            id={`title-${section.id}`}
            value={section.title}
            onChange={(e) => onUpdate(section.id, "title", e.target.value)}
            placeholder={intl.formatMessage({
              id: "template.placeholders.sectionTitle",
              defaultMessage: "Enter section title",
            })}
            className={
              !section.title && (showError || parsingError)
                ? "border-destructive"
                : ""
            }
          />
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onRemove(section.id)}
          disabled={isRemoveDisabled}
          className="text-muted-foreground hover:text-destructive disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>

      <div className="space-y-3">
        {section.sectionType === "docList" ? (
          <div className="space-y-3">
            <div className="text-sm font-medium text-muted-foreground">
              <FormattedMessage
                id="template.docList.description"
                defaultMessage="Lists all documents from selected categories"
              />
            </div>
            {availableCategories.length > 0 ? (
              <div className="space-y-3 border rounded-md p-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">
                    {allSelected ? (
                      <FormattedMessage
                        id="template.allCategoriesSelected"
                        defaultMessage="All categories selected"
                      />
                    ) : noneSelected ? (
                      <FormattedMessage
                        id="template.noCategoriesSelected"
                        defaultMessage="No categories selected"
                      />
                    ) : (
                      <FormattedMessage
                        id="template.categoriesSelected"
                        defaultMessage="{count} of {total} selected"
                        values={{
                          count: selectedCount,
                          total: availableCategories.length,
                        }}
                      />
                    )}
                  </span>
                  <div className="flex gap-1">
                    {!allSelected && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={handleSelectAll}
                        className="h-6 px-2 text-xs"
                      >
                        <FormattedMessage
                          id="template.selectAll"
                          defaultMessage="Select All"
                        />
                      </Button>
                    )}
                    {selectedCount > 0 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={handleClearAll}
                        className="h-6 px-2 text-xs"
                      >
                        <FormattedMessage
                          id="template.clearAll"
                          defaultMessage="Clear All"
                        />
                      </Button>
                    )}
                  </div>
                </div>
                <ScrollArea className="h-[200px]">
                  <div className="space-y-1 pr-4">
                    {availableCategories.map((category) => {
                      const isExcluded = excludedNames.includes(category.name);
                      const isSelected = !isExcluded;
                      return (
                        <div
                          key={category.id}
                          className="flex items-center w-full uppercase rounded"
                          style={{
                            backgroundColor: isSelected
                              ? deserializeColor(category.color || "").hex ||
                                "#e5e7eb"
                              : hexToRgba(
                                  deserializeColor(category.color || "").hex ||
                                    "#e5e7eb",
                                  0.1
                                ),
                          }}
                        >
                          <label className="flex items-center p-2 pl-3 cursor-pointer w-full">
                            <Checkbox
                              id={`${section.id}-category-${category.id}`}
                              checked={isSelected}
                              onCheckedChange={() =>
                                onCategoryToggle?.(section.id, category.name)
                              }
                              className="relative h-4 w-4 shrink-0 rounded-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:text-primary-foreground cursor-pointer data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600 border shadow-none bg-background border-border mr-2"
                            />
                            <span
                              className="text-xs"
                              style={{
                                color: isSelected ? "white" : "inherit",
                              }}
                            >
                              {category.name}
                            </span>
                          </label>
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              </div>
            ) : (
              <div className="px-3 py-2 bg-muted text-muted-foreground rounded-md text-sm">
                <FormattedMessage
                  id="template.docList.noCategories"
                  defaultMessage="No categories available. Categories will be loaded from your organization."
                />
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="space-y-1">
              <Label htmlFor={`purpose-${section.id}`}>
                <FormattedMessage
                  id="template.sectionPurpose"
                  defaultMessage="Purpose"
                />
              </Label>
              <AutoResizeTextarea
                id={`purpose-${section.id}`}
                value={section.purpose}
                onChange={(e) =>
                  onUpdate(section.id, "purpose", e.target.value)
                }
                placeholder={intl.formatMessage({
                  id: "template.placeholders.sectionPurpose",
                  defaultMessage: "Describe the purpose of this section",
                })}
                maxLength={MAX_SECTION_CHARS}
                className={
                  !section.purpose && (showError || parsingError)
                    ? "border-destructive"
                    : section.purpose.length > MAX_SECTION_CHARS
                      ? "border-destructive"
                      : ""
                }
              />
              <p
                className={`text-xs text-right ${
                  section.purpose.length > MAX_SECTION_CHARS
                    ? "text-destructive"
                    : "text-muted-foreground"
                }`}
              >
                {section.purpose.length}/{MAX_SECTION_CHARS}
              </p>
            </div>

            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <Label htmlFor={`prompt-${section.id}`}>
                  <FormattedMessage
                    id="template.sectionPrompt"
                    defaultMessage="Prompt"
                  />
                </Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => onToggleCollapse(section.id)}
                  className="h-6 px-2 text-muted-foreground hover:text-foreground"
                >
                  {isCollapsed ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronUp className="w-4 h-4" />
                  )}
                </Button>
              </div>
              {!isCollapsed && (
                <>
                  <AutoResizeTextarea
                    id={`prompt-${section.id}`}
                    value={section.prompt}
                    onChange={(e) =>
                      onUpdate(section.id, "prompt", e.target.value)
                    }
                    placeholder={intl.formatMessage({
                      id: "template.placeholders.sectionPrompt",
                      defaultMessage: "Enter the prompt for this section",
                    })}
                    maxLength={MAX_SECTION_CHARS}
                    className={`min-h-[80px] ${
                      !section.prompt && (showError || parsingError)
                        ? "border-destructive"
                        : section.prompt.length > MAX_SECTION_CHARS
                          ? "border-destructive"
                          : ""
                    }`}
                  />
                  <p
                    className={`text-xs text-right ${
                      section.prompt.length > MAX_SECTION_CHARS
                        ? "text-destructive"
                        : "text-muted-foreground"
                    }`}
                  >
                    {section.prompt.length}/{MAX_SECTION_CHARS}
                  </p>
                </>
              )}
              {isCollapsed && (
                <div
                  className="text-sm text-muted-foreground bg-muted/30 rounded p-2 border border-dashed cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => onToggleCollapse(section.id)}
                >
                  {section.prompt ? (
                    <div className="space-y-1">
                      <div className="font-medium">
                        <FormattedMessage
                          id="template.promptCollapsed"
                          defaultMessage="Prompt is hidden (click to expand)"
                        />
                      </div>
                      <div className="text-xs truncate opacity-70">
                        {section.prompt}
                      </div>
                    </div>
                  ) : (
                    <FormattedMessage
                      id="template.promptEmpty"
                      defaultMessage="Click to add prompt"
                    />
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );

  return isDragging && typeof document !== "undefined"
    ? createPortal(content, document.body)
    : content;
};
